// @ts-strict-ignore
import { context, Dictionary, targetingService } from '@ad-engine/core';
import { PrebidNativeConfig } from '../native';
import { PrebidAdapter } from '../prebid-adapter';
import { PrebidAdSlotConfig } from '../prebid-models';

export class Appnexus extends PrebidAdapter {
	static bidderName = 'appnexus';
	placements: Dictionary<string>;
	bidderSettings = {
		storageAllowed: true,
	};

	constructor(options) {
		super(options);

		/* Added in to prevent the default -1 value from being assigned. This should come from Audigent,
		but the data loads in too late. We need this value to be default for a specific Microsoft campaign, until
		we can figure out how to get the Audigent data before the first bid call occurs. */
		const appNexusDefaultAuSeg = context.get('options.forceDefaultAuSegOnAppNexus');
		if (appNexusDefaultAuSeg) {
			this.pageTargeting['AU_SEG'] = [appNexusDefaultAuSeg];
		}

		this.placements = options.placements;
	}

	get bidderName(): string {
		return Appnexus.bidderName;
	}

	isNativeModeOn(): boolean {
		return context.get('bidders.prebid.appnexusNative.enabled');
	}

	prepareConfigForAdUnit(
		code,
		{ sizes, placementId, position = 'mobile' }: PrebidAdSlotConfig,
	): PrebidAdUnit {
		const newSizes = this.filterSizesForRefreshing(code, sizes);

		if (context.get(`slots.${code}.isNative`)) {
			if (context.get('bidders.prebid.native.enabled') && this.isNativeModeOn()) {
				return this.prepareNativeConfig(code, { sizes: newSizes, placementId, position });
			}
		}

		return this.prepareStandardConfig(code, { sizes: newSizes, placementId, position });
	}

	prepareStandardConfig(code, { sizes, placementId, position }: PrebidAdSlotConfig): PrebidAdUnit {
		return {
			code,
			mediaTypes: {
				banner: {
					sizes,
				},
			},
			ortb2Imp: this.getOrtb2Imp(code),
			bids: this.getBids(code, { sizes, placementId, position }),
		};
	}

	prepareNativeConfig(code, { sizes, placementId, position }: PrebidAdSlotConfig): PrebidAdUnit {
		return {
			code,
			mediaTypes: {
				native: PrebidNativeConfig.getPrebidNativeMediaTypes(position),
			},
			ortb2Imp: this.getOrtb2Imp(code),
			bids: this.getBids(code, { sizes, placementId, position }),
		};
	}

	getBids(code, { placementId, position = 'mobile' }: PrebidAdSlotConfig): PrebidBid[] {
		if (Array.isArray(placementId) && placementId.length > 0) {
			return placementId.map((id) => ({
				bidder: this.bidderName,
				params: {
					placementId: id,
					keywords: {
						...this.getTargeting(code),
					},
				},
			}));
		}

		return [
			{
				bidder: this.bidderName,
				params: {
					placementId: placementId || this.getPlacement(position),
					keywords: {
						...this.getTargeting(code),
					},
				},
			},
		];
	}

	getPlacement(position): string {
		let placement = position;

		if (position === 'mobile') {
			const vertical = targetingService.get('mappedVerticalName');

			placement = vertical && this.placements[vertical] ? vertical : 'other';
		}

		return this.placements[placement];
	}
}
